import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useGlobalMutation, useGlobalQuery } from '@/state/query-client/hooks';
import { useToast } from '@/hooks/use-toast';
import {
  createPotentialProfile,
  deletePotentialProfile,
  getDepartmentsLookup,
  getEmployeesLookup,
  getPotentialProfiles,
  transferToConditionalOffer,
} from '../services/profile.service';
import {
  GetPotentialProfilesParams,
  InterviewPanelMember,
  PotentialProfile,
  PotentialProfileInsertInput,
} from '../types/profile.types';

const invalidateProfileQueries = (queryClient: ReturnType<typeof useQueryClient>) =>
  queryClient.invalidateQueries({ queryKey: ['hr-potential-profiles'] });

export const useGetPotentialProfiles = (params: GetPotentialProfilesParams) => {
  return useGlobalQuery({
    queryKey: ['hr-potential-profiles', params],
    queryFn: () => getPotentialProfiles(params),
    staleTime: 60 * 1000,
  });
};

export const useGetDepartmentsLookup = () => {
  return useGlobalQuery({
    queryKey: ['hr-departments-lookup'],
    queryFn: () => getDepartmentsLookup(),
    staleTime: 30 * 60 * 1000,
  });
};

export const useGetEmployeesLookup = (keyword: string) => {
  return useGlobalQuery({
    queryKey: ['hr-profiles-employees-lookup', keyword],
    queryFn: () => getEmployeesLookup(keyword),
    staleTime: 60 * 1000,
  });
};

export const useCreatePotentialProfile = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: (input: PotentialProfileInsertInput) => createPotentialProfile(input),
    onSuccess: () => {
      invalidateProfileQueries(queryClient);
      toast({ title: t('PROFILE_CREATED') });
    },
  });
};

export const useDeletePotentialProfile = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: (itemId: string) => deletePotentialProfile(itemId),
    onSuccess: () => {
      invalidateProfileQueries(queryClient);
      toast({ title: t('PROFILE_DELETED') });
    },
  });
};

export const useTransferToConditionalOffer = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: (params: {
      profile: PotentialProfile;
      departmentId: string;
      departmentName: string;
      interviewPanel: InterviewPanelMember[];
    }) => transferToConditionalOffer(params),
    onSuccess: () => {
      invalidateProfileQueries(queryClient);
      toast({ title: t('TRANSFERRED_TO_CONDITIONAL_OFFER') });
    },
  });
};
